/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { CompanyContact } from 'types/entities/CompanyContact'
import { UiTableActionsCell, UiTableCell, UiTableRow, } from 'components/Ui/UiTable'
import { EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid'
import { getCompanyContactDisplayName } from 'utils/companiesUtils'
import { isGenericCompanyContact } from 'utils/contacts/genericContactUtils'

interface EditCompanyModalContactItemProps {
    contact: CompanyContact
    hasDuplicateEmail?: boolean
    onView: (contact: CompanyContact) => void
    onEdit: (contact: CompanyContact) => void
    onDelete: (contact: CompanyContact) => void
}

const EditCompanyModalContactItem: React.FC<
    EditCompanyModalContactItemProps
> = ({ contact, hasDuplicateEmail, onView, onEdit, onDelete }) => {
    const { t } = useTranslation()

    const handleView = (e: React.MouseEvent) => {
        e.stopPropagation()
        onView(contact)
    }

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation()
        onEdit(contact)
    }

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation()
        onDelete(contact)
    }

    return (
        <UiTableRow className="EditCompanyModalContactItem" key={contact.id}>
            <UiTableCell>
                <div className="flex flex-col gap-0.5">
                    <span>
                        {getCompanyContactDisplayName(contact) || '-'}
                    </span>
                    {isGenericCompanyContact(contact) && (
                        <span className="text-[10px] font-semibold text-blue-700 dark:text-blue-300">
                            {t('Generic contact')}
                        </span>
                    )}
                </div>
            </UiTableCell>
            <UiTableCell>
                {contact.email ? (
                    <div className="flex flex-col gap-0.5">
                        <span className={hasDuplicateEmail ? 'text-orange-700 dark:text-orange-300' : undefined}>
                            {contact.email}
                        </span>
                        {hasDuplicateEmail && (
                            <span className="text-[10px] font-semibold text-orange-700 dark:text-orange-300">
                                {t('Duplicate email')}
                            </span>
                        )}
                    </div>
                ) : (
                    <span className="text-gray-400">-</span>
                )}
            </UiTableCell>
            <UiTableCell>
                {contact.phone || <span className="text-gray-400">-</span>}
            </UiTableCell>
            <UiTableActionsCell>
                <button
                    onClick={handleView}
                    className="p-1.5 bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 rounded transition-colors shrink-0"
                    title={t('View Detail') as string}
                >
                    <EyeIcon className="w-4 h-4 text-white" />
                </button>
                <button
                    onClick={handleEdit}
                    className="p-1.5 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 rounded transition-colors shrink-0"
                    title={t('Edit') as string}
                >
                    <PencilIcon className="w-4 h-4 text-white" />
                </button>
                <button
                    onClick={handleDelete}
                    className="p-1.5 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 rounded transition-colors shrink-0"
                    title={t('Delete') as string}
                >
                    <TrashIcon className="w-4 h-4 text-white" />
                </button>
            </UiTableActionsCell>
        </UiTableRow>
    )
}

export default EditCompanyModalContactItem
